import { cn } from "@/lib/utils/cn";
import { computeSpans } from "./grid";
import type { ResolvedWidget } from "./resolve";
import { useGridColumns } from "./useGridColumns";
import { WidgetBoundary } from "./WidgetBoundary";

/**
 * The dashboard as people use it: the chosen widgets on a 12-column grid
 * (6 on a tablet, 1 on a phone) with one gap everywhere and no controls. The
 * saved layout only says order and size; the spans for this screen are worked
 * out here, so resizing the window reflows the dashboard without touching
 * what was saved.
 */
export function DashboardGrid({ widgets }: { widgets: ResolvedWidget[] }) {
  const { ref, columns } = useGridColumns<HTMLDivElement>();
  const spans = computeSpans(
    widgets.map((w) => w.size),
    columns,
  );
  return (
    <div
      ref={ref}
      data-testid="dashboard-grid"
      data-columns={columns}
      className={cn("grid gap-4 xl:gap-5")}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {widgets.map(({ item, meta, Component, size }, i) => (
        <div key={item.widget_id} data-widget-id={item.widget_id} data-span={spans[i]} className="min-w-0" style={{ gridColumn: `span ${spans[i]} / span ${spans[i]}` }}>
          <WidgetBoundary name={meta.name}>
            <Component size={size} />
          </WidgetBoundary>
        </div>
      ))}
    </div>
  );
}
